import { useEffect, useRef, useState } from "react";
import { deleteCategory, listCategories } from "../../services/category/category.service";
import type { CategorySummary } from "../../services/category/category.types";
import { TOAST_ERROR_MS } from "../../constants/timeouts";
import { addAssociatedCustomers, getCategoryErrorMessage, hasAssignedSites } from "./categoryCustomers";

export const CATEGORY_DELETE_BLOCKED = "This category cannot be deleted while sites are assigned. From the site details page, reassign the sites first.";

export function useCategories() {
    const [categories, setCategories] = useState<CategorySummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [modalError, setModalError] = useState<string | null>(null);
    const [success, setSuccess] = useState<{ message: string } | null>(null);
    const requestId = useRef(0);

    useEffect(() => {
        let mounted = true;
        const current = ++requestId.current;
        const load = async () => {
            try {
                const response = await listCategories();
                if (!response.success || !response.data) {
                    throw new Error(response.error?.message || "Failed to load categories");
                }
                const result = await addAssociatedCustomers(response.data);
                if (mounted && current === requestId.current) {
                    setCategories(result.categories);
                    if (result.errors.length) setError(result.errors.join("\n"));
                }
            } catch (error: unknown) {
                if (mounted) setError(getCategoryErrorMessage(error, "Failed to load categories"));
            } finally {
                if (mounted) setLoading(false);
            }
        };
        void load();
        return () => { mounted = false; requestId.current += 1; };
    }, []);

    useEffect(() => {
        if (!success) return;
        const timeout = setTimeout(() => setSuccess(null), TOAST_ERROR_MS);
        return () => clearTimeout(timeout);
    }, [success]);

    const saved = async (category: CategorySummary, editing: boolean, warning?: string) => {
        setSubmitting(true);
        setError(null);
        setSuccess(null);
        // The category is already persisted. Retain it even if customer hydration fails.
        setCategories(previous => editing
            ? previous.map(item => item.id === category.id ? { ...item, ...category } : item)
            : [...previous, category]);
        const result = await addAssociatedCustomers([category]);
        setCategories(previous => previous.map(item => item.id === category.id ? result.categories[0] : item));
        const failures = [warning, ...result.errors].filter(Boolean);
        if (failures.length) setError(`Category saved, but some updates failed: ${failures.join("\n")}`);
        else setSuccess({ message: editing ? "Category updated" : "Category created" });
        setSubmitting(false);
    };

    const remove = async (category: CategorySummary): Promise<boolean> => {
        if (hasAssignedSites(category)) {
            setModalError(CATEGORY_DELETE_BLOCKED);
            return false;
        }
        setSubmitting(true);
        setModalError(null);
        setError(null);
        setSuccess(null);
        try {
            const response = await deleteCategory(category.id);
            if (!response.success) throw new Error(response.error?.message || "Delete failed");
            setCategories(previous => previous.filter(item => item.id !== category.id));
            setSuccess({ message: "Category deleted" });
            return true;
        } catch (error: unknown) {
            setModalError(getCategoryErrorMessage(error, "Delete failed"));
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    return { categories, loading, submitting, error, modalError, success, setError, setModalError, setSuccess, saved, remove };
}
